import type { ApiPerformanceInbox } from '@/client/types/marketing/api';

type Volume = NonNullable<ApiPerformanceInbox['volume']>;
type ResponseTime = NonNullable<ApiPerformanceInbox['response_time']>;
type Heatmap = NonNullable<ApiPerformanceInbox['heatmap']>;

/** Every day in [from, to], with the vendor's sparse rows filled with 0. */
export function dailyVolume(volume: Volume | null, from: string, to: string) {
  const byDate = new Map((volume?.timeseries ?? []).map((r) => [r.date.slice(0, 10), r]));
  const out: { date: string; received: number; sent: number; read: number; failed: number }[] = [];
  for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    const r = byDate.get(key);
    out.push({ date: key, received: r?.received ?? 0, sent: r?.sent ?? 0, read: r?.read ?? 0, failed: r?.failed ?? 0 });
  }
  return out;
}

/** Share of replied messages answered within each target, from the histogram. */
export const RESPONSE_TARGETS = [
  { label: '≤ 5 min', seconds: 300, target: 50 },
  { label: '≤ 15 min', seconds: 900, target: 80 },
  { label: '≤ 1 hour', seconds: 3600, target: 95 },
] as const;

export function responseTargets(rt: ResponseTime | null) {
  const total = rt?.summary.sampleSize ?? 0;
  return RESPONSE_TARGETS.map((t) => {
    const within = (rt?.histogram ?? [])
      .filter((b) => b.upperSeconds != null && b.upperSeconds <= t.seconds)
      .reduce((s, b) => s + b.count, 0);
    const pct = total > 0 ? Math.round((within / total) * 100) : null;
    return { ...t, pct, met: pct != null && pct >= t.target };
  });
}

export const HEAT_DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** 7x24 grid (row 0 = Monday) of received + sent; the vendor sends only non-empty cells. */
export function heatGrid(heat: Heatmap | null) {
  const grid = HEAT_DOW.map(() => Array.from({ length: 24 }, () => ({ received: 0, sent: 0 })));
  for (const b of heat?.buckets ?? []) {
    const row = grid[b.dow - 1];
    if (!row || b.hour < 0 || b.hour > 23) continue;
    row[b.hour].received += b.received;
    row[b.hour].sent += b.sent;
  }
  const max = Math.max(1, ...grid.flat().map((c) => c.received + c.sent));
  return { grid, max };
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}d ${Math.floor((s % 86400) / 3600)}h`;
}
